import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { IonText } from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import CheckboxGroup from '@components/fields/CheckboxGroup';
import FormButtons from '@components/fields/FormButtons';
import RichTextArea from '@components/fields/RichTextArea';
import Select from '@components/fields/Select';
import styles from '@components/forms/CarePlanActionItemForm.module.css';
import Form from '@components/forms/Form';
import FormCondition from '@components/forms/FormCondition';
import useCreateCarePlanActionItem from '@hooks/mutations/useCreateCarePlanActionItem';
import useMyClient from '@hooks/useMyClient';
import {
  CarePlanActionItemFragment as CarePlanActionItem,
  CarePlanActionItemCtaEnum,
  CarePlanActionItemUpdateDocument,
  CarePlanActionItemUpdateMutation,
  CarePlanActionItemUpdateMutationVariables,
  GetJourneyWithPlanQuery,
  GoalFragment
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';

type FormData = {
  assignmentIds?: string[] | undefined;
  cta: CarePlanActionItemCtaEnum | null;
  goalId?: string | null | undefined;
  templateId: string | null;
  title: string;
};

type Props = {
  autofocus: boolean;
  carePlanActionItem?: CarePlanActionItem | null;
  goal?: GoalFragment | null;
  journey: GetJourneyWithPlanQuery['journey'];
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const CarePlanActionItemForm = ({
  carePlanActionItem = undefined,
  goal,
  journey,
  onCancel,
  onError,
  onSuccess
}: Props) => {
  const myClient = useMyClient();
  const intl = useIntl();

  const assigneeOptions = journey.clientRoles.map(clientRole => ({
    label: clientRole.client.firstName,
    value: clientRole.client.id
  }));

  const calculateInitialValues = useCallback(
    () => ({
      assignmentIds:
        carePlanActionItem?.assignments && carePlanActionItem.assignments.length > 1
          ? carePlanActionItem?.assignments?.map(assignee => assignee.id)
          : journey.clientRoles.length === 1
          ? [journey.clientRoles[0].client.id]
          : [],
      cta: carePlanActionItem?.cta ?? null,
      goalId: carePlanActionItem?.goal.id ?? goal?.id ?? null,
      templateId:
        carePlanActionItem?.carePlanTaskTemplateId ?? carePlanActionItem?.worksheet?.worksheetTemplate.id ?? null,
      title: carePlanActionItem?.title ?? ''
    }),
    [
      carePlanActionItem?.assignments,
      carePlanActionItem?.cta,
      carePlanActionItem?.goal.id,
      carePlanActionItem?.carePlanTaskTemplateId,
      carePlanActionItem?.worksheet?.worksheetTemplate.id,
      carePlanActionItem?.title,
      journey.clientRoles,
      goal?.id
    ]
  );

  const initialValues = calculateInitialValues();

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const createCarePlanActionItem = useCreateCarePlanActionItem(journey);

  const [updateCarePlanActionItem] = useMutation<
    CarePlanActionItemUpdateMutation,
    CarePlanActionItemUpdateMutationVariables
  >(CarePlanActionItemUpdateDocument);

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      assignmentIds: yup
        .array(yup.string().defined())
        .min(1, intl.formatMessage({ id: 'errors.carePlanActionItem.assigneeIds.min' })),
      cta: yup.string().nullable().defined().oneOf(Object.values(CarePlanActionItemCtaEnum)),
      goalId: yup.string().required(intl.formatMessage({ id: 'errors.carePlanActionItem.goalId.blank' })),
      templateId: yup.string().nullable().defined(),
      title: yup.string().required(intl.formatMessage({ id: 'errors.carePlanActionItem.title.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const cta = form.watch('cta');

  const goalOptions = journey.goals.map(goal => ({
    label: goal.title,
    value: goal.id
  }));

  useEffect(() => {
    const values = calculateInitialValues();
    form.reset(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateInitialValues]);

  const assignmentIds = form.watch('assignmentIds');

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    if (carePlanActionItem?.id) {
      return updateCarePlanActionItem({
        variables: {
          ...form.getValues(),
          assignmentIds,
          id: carePlanActionItem.id
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.carePlanActionItemUpdate,
          intl,
          onError,
          onSuccess: () => {
            form.reset();
            onSuccess();
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.carePlanActionItem'
        });
      });
    }
    return createCarePlanActionItem({
      variables: {
        assignmentIds,
        cta: form.getValues().cta,
        goalId: form.getValues().goalId ?? '',
        title: form.getValues().title
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.carePlanActionItemCreate,
        intl,
        onError,
        onSuccess: () => {
          form.reset();
          onSuccess();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.carePlanActionItem'
      });
    });
  }, [
    form,
    carePlanActionItem?.id,
    createCarePlanActionItem,
    assignmentIds,
    updateCarePlanActionItem,
    intl,
    onError,
    onSuccess
  ]);

  return (
    <>
      <Form data-test="action-item-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
        <BaseErrors errors={baseErrors} />

        <RichTextArea<FormData>
          control={form.control}
          error={form.formState.errors.title}
          journeyId={journey.id}
          label={intl.formatMessage({ id: 'forms.carePlanActionItem.titleLabel' })}
          name="title"
          required
          sharableURLSHidden={!!myClient}
          textMenuHidden
        />
        {cta && (
          <div className={styles.margin}>
            <IonText className={styles.buttonIncluded}>
              <FormattedMessage id={`models.actionItemCtasIncludes.${cta}`} />
            </IonText>
          </div>
        )}
        <FormCondition<FormData> condition={assigneeOptions.length > 1} fieldName="assignmentIds" form={form}>
          <CheckboxGroup<FormData>
            control={form.control}
            errors={form.formState.errors.assignmentIds}
            label={intl.formatMessage({ id: 'forms.carePlanActionItem.assigneeLabel' })}
            name="assignmentIds"
            options={assigneeOptions}
            required
          />
        </FormCondition>
        <FormCondition<FormData> condition={!goal} fieldName="goalId" form={form}>
          <Select
            control={form.control}
            disabled={!!goal}
            error={form.formState.errors.goalId}
            label={intl.formatMessage({ id: 'forms.carePlanActionItem.goalLabel' })}
            name="goalId"
            options={goalOptions}
            required
          />
        </FormCondition>
        <FormButtons hookForm={form} onCancel={onCancel} saveKey={carePlanActionItem ? undefined : 'dictionary.add'} />
      </Form>
    </>
  );
};

export default CarePlanActionItemForm;
